import {
  type Maybe,
  type FilterValue,
} from "@shopify/hydrogen-react/storefront-api-types";

import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { formatMoney } from "@root/tablescape/utilities";
import {
  type AdjacentCollectionsConnection,
  CollectionContentCardType,
  type CollectionContentCard,
  type CollectionContentCardConnection,
  type OPProductConnection,
  type OPProductVariantConnection,
} from "@tablescape/state";

import { isStringArray } from "./general";
import {
  buildColorTaxonomyMappingsFromFilters,
  extractTaxonomyValueGID,
  getTaxonomyGIDFromColorName,
} from "./taxonomyColorMapper";

export const generatePriceFilterValue = (
  min: number,
  max: number
): FilterValue => ({
  id: COLLECTION_FILTER_IDS.PRICE,
  label: `${formatMoney(
    min * 100,
    // eslint-disable-next-line no-template-curly-in-string
    "${{ amount_no_trailing_zeroes }}",
    false
  )} - ${formatMoney(
    max * 100,
    // eslint-disable-next-line no-template-curly-in-string
    "${{ amount_no_trailing_zeroes }}",
    false
  )}`,
  count: 0,
  input: JSON.stringify({
    price: {
      min,
      max,
    },
  }),
});

export const formatCollectionContentCard = (
  contentCardConnection: CollectionContentCardConnection
) => ({
  background: contentCardConnection.background?.value,
  ctaText: contentCardConnection.ctaText?.value,
  ctaUrl: contentCardConnection.ctaUrl?.value,
  description: contentCardConnection.description?.value,
  image: contentCardConnection.image?.reference,
  mobileImage: contentCardConnection.mobileImage?.reference,
  title: contentCardConnection.title?.value,
  videoControlColor: contentCardConnection.videoControlColor?.value,
  videoMp4: contentCardConnection.videoMp4?.reference,
  videoWebm: contentCardConnection.videoWebm?.reference,
});

export const formatCollectionContentCards = (
  nodes: {
    contentCard: { reference: CollectionContentCardConnection };
    id: string;
    position: { value: string };
  }[]
) =>
  nodes.map((node) => ({
    contentCard: formatCollectionContentCard(node.contentCard.reference),
    id: node.id,
    position: node.position.value,
  }));

export const formatAdjacentCollections = (
  adjacentCollections?: AdjacentCollectionsConnection,
  disableAdjacentCollectionTitleOverride?: Maybe<{ value: string }>
) => {
  // Check if adjacent collection title override is disabled for the parent collection
  // Move this outside the map for performance since it's constant for all items
  const isAdjacentTitleOverrideDisabled =
    disableAdjacentCollectionTitleOverride?.value === "true";

  return (
    adjacentCollections?.references.nodes.map(
      ({ adjacentCollectionTitle, title, titleOverride, ...rest }) => {
        // If adjacent title override is disabled, use: titleOverride → title
        if (isAdjacentTitleOverrideDisabled) {
          return {
            title: titleOverride?.value || title,
            ...rest,
          };
        }

        // Otherwise, use the full priority order: adjacentCollectionTitle → titleOverride → title
        return {
          title:
            adjacentCollectionTitle?.value || titleOverride?.value || title,
          ...rest,
        };
      }
    ) ?? []
  );
};

export const getCollectionContentCardType = ({
  description,
  image,
  title,
  videoMp4,
  videoWebm,
}: CollectionContentCard) => {
  if (videoMp4 || videoWebm) {
    return CollectionContentCardType.VIDEO_CARD;
  } else if (image && !(title && description)) {
    return CollectionContentCardType.IMAGE_CARD;
  } else if (image && (title || description)) {
    return CollectionContentCardType.IMAGE_TEXT_CARD;
  } else {
    return CollectionContentCardType.TEXT_CARD;
  }
};

type ColorPriorityVariant = {
  colorGroup?: Maybe<string[]>;
  selectedOptions: ReadonlyArray<{ name: string; value: string }>;
};

export const prioritizeColorOptions = <T extends { name: string }>(
  optionValues: readonly T[],
  appliedColorFilters: FilterValue[],
  variants: ReadonlyArray<ColorPriorityVariant>
): T[] => {
  if (appliedColorFilters.length === 0) {
    return [...optionValues];
  }

  // Extract taxonomy GIDs from applied filters and create priority map
  // Priority map uses GIDs (not names) so we can match regardless of localization
  const colorGIDPriorityMap = new Map<string, number>();
  appliedColorFilters.forEach((filter, index) => {
    const gid = extractTaxonomyValueGID(filter);
    if (gid) {
      // Use GID directly for priority mapping (highest priority = latest filter = higher index)
      colorGIDPriorityMap.set(gid, index);
    }
  });

  // Build mapping from color names to GIDs for converting variant.colorGroup
  const { nameToGid } =
    buildColorTaxonomyMappingsFromFilters(appliedColorFilters);

  // Precompute: optionName -> variant lookup (avoids nested find() in sort)
  const optionNameToVariant = new Map<
    string,
    ColorPriorityVariant | undefined
  >();
  for (const optionValue of optionValues) {
    const variant = variants.find((v) =>
      v.selectedOptions.some((opt) => opt.value === optionValue.name)
    );
    optionNameToVariant.set(optionValue.name, variant);
  }

  // Precompute: optionName -> priority score (avoids repeated getVariantColorPriority calls)
  const optionNameToPriority = new Map<string, number>();
  for (const optionValue of optionValues) {
    const variant = optionNameToVariant.get(optionValue.name);
    const priority = getVariantColorPriority(
      variant,
      colorGIDPriorityMap,
      nameToGid
    );
    optionNameToPriority.set(optionValue.name, priority);
  }

  // Sort using O(1) lookups instead of nested finds
  const sorted = [...optionValues].sort((a, b) => {
    const aPriority = optionNameToPriority.get(a.name) ?? -1;
    const bPriority = optionNameToPriority.get(b.name) ?? -1;
    // Sort by priority (higher priority = lower index)
    return bPriority - aPriority;
  });

  // Deduplicate by color name - keep only the first occurrence of each color name
  const seen = new Set<string>();
  return sorted.filter((optionValue) => {
    if (seen.has(optionValue.name)) {
      return false; // Skip duplicates
    }
    seen.add(optionValue.name);
    return true;
  });
};

// Helper function to get variant's color priority score
// Converts variant.colorGroup (English color names) to GIDs and matches against filter GIDs
const getVariantColorPriority = (
  variant: ColorPriorityVariant | undefined,
  colorGIDPriorityMap: Map<string, number>,
  nameToGidMap: Map<string, string>
): number => {
  if (!variant?.colorGroup) return -1; // No priority

  let maxPriority = -1;

  // Convert variant.colorGroup (English color names) to GIDs and check against priority map
  variant.colorGroup.forEach((colorName: string) => {
    // Convert English color name to taxonomy GID
    const gid = getTaxonomyGIDFromColorName(colorName, nameToGidMap);
    if (gid) {
      // Match GID against filter priority map
      const priority = colorGIDPriorityMap.get(gid);
      if (priority !== undefined && priority > maxPriority) {
        maxPriority = priority;
      }
    }
  });

  return maxPriority;
};

/**
 * Determines if a product has at least one eligible variant by the hidden status, variant match filter, color filters, and price filter
 * @param product - The product to check
 * @param variantMatchFilter - The variant match filter to check
 * @param appliedFilters - All currently applied filters
 * @returns boolean
 */
export const filterProductByVariantEligibility = (
  product: OPProductConnection,
  variantMatchFilter: string | undefined,
  appliedFilters: FilterValue[]
): boolean => {
  // Extract color filters from applied filters
  const appliedColorFilters = appliedFilters.filter((filter) =>
    filter.id.startsWith(COLLECTION_FILTER_IDS.COLOR)
  );

  // Extract price filter from applied filters
  const priceFilterValue = appliedFilters.find(
    (f) => f.id === COLLECTION_FILTER_IDS.PRICE
  );
  let priceFilter: { max: number; min: number } | null = null;
  if (priceFilterValue) {
    try {
      const parsed = JSON.parse(String(priceFilterValue.input));
      priceFilter = parsed.price || null;
    } catch {
      // If parsing fails, priceFilter remains null
    }
  }

  let visibleVariants = product.variants.nodes.filter(
    (variant: OPProductVariantConnection) =>
      !variant.hidden?.value || variant.hidden?.value !== "true"
  );

  if (visibleVariants.length === 0) {
    return false;
  }

  if (variantMatchFilter) {
    visibleVariants = visibleVariants.filter(
      (variant: OPProductVariantConnection) => {
        return variant.collectionNames?.value?.includes(variantMatchFilter);
      }
    );
  }

  if (appliedColorFilters.length > 0) {
    // Extract taxonomy GIDs from applied filters (precompute as Set for O(1) membership)
    const appliedColorGIDs = appliedColorFilters
      .map(extractTaxonomyValueGID)
      .filter(Boolean) as string[]; // Remove null values
    const appliedColorGIDsSet = new Set<string>(appliedColorGIDs);

    // Build a mapping of color names to taxonomy GIDs from the filter values
    // This includes static fallback mappings, so all standard colors will be mapped
    // colorGroup stores English color names (e.g., "Blue", "Green"), so we convert them to GIDs for comparison
    const { nameToGid } =
      buildColorTaxonomyMappingsFromFilters(appliedColorFilters);

    // Local cache for parsed color groups per variant for this evaluation
    const colorGroupCache = new Map<string, string[] | null>();

    const parseColorGroupOnce = (
      cacheKey: string,
      raw: string | undefined
    ): string[] | null => {
      if (!raw) return null;
      const cached = colorGroupCache.get(cacheKey);
      if (cached !== undefined) return cached;

      let result: string[] | null = null;
      try {
        const parsed = JSON.parse(raw);
        if (isStringArray(parsed)) {
          result = parsed;
        } else if (typeof parsed === "string") {
          result = [parsed];
        }
      } catch {
        if (raw.trim()) result = [raw];
      }

      colorGroupCache.set(cacheKey, result);
      return result;
    };

    visibleVariants = visibleVariants.filter(
      (variant: OPProductVariantConnection) => {
        const colorGroupValue = variant.colorGroup?.value;
        if (!colorGroupValue) return false;

        // Use colorGroupValue as cache key (unique enough per variant)
        const colorNames = parseColorGroupOnce(
          colorGroupValue,
          colorGroupValue
        );
        if (!colorNames || colorNames.length === 0) return false;

        for (const colorName of colorNames) {
          const gid = getTaxonomyGIDFromColorName(colorName, nameToGid);
          if (gid && appliedColorGIDsSet.has(gid)) return true;
        }
        return false;
      }
    );
  }

  if (visibleVariants.length === 0) {
    return false;
  }

  // Apply price filter: exclude if only hidden variants match price range
  if (priceFilter) {
    const { max, min } = priceFilter;
    const hasMatchingVisiblePrice = visibleVariants.some((variant) => {
      const priceAmount = variant.price?.amount;
      if (!priceAmount) return true; // Graceful degradation

      const price = parseFloat(priceAmount);
      if (isNaN(price)) return true; // Safe default

      return price >= min && price <= max;
    });

    if (!hasMatchingVisiblePrice) {
      return false;
    }
  }

  return true;
};

/**
 * Whether merchandising content cards should render in the collection grid.
 * Computed once in `useCollectionQuery` and passed to CollectionGrid.
 */
export function shouldShowCollectionContentCards({
  appliedFiltersCount,
  appliedSortName,
  contentCards,
  defaultSortName,
}: {
  appliedFiltersCount: number;
  appliedSortName: string | undefined;
  contentCards: ReadonlyArray<unknown> | null | undefined;
  defaultSortName: string | undefined;
}): boolean {
  return (
    !!contentCards &&
    contentCards.length > 0 &&
    appliedFiltersCount === 0 &&
    (appliedSortName === undefined || appliedSortName === defaultSortName)
  );
}

/**
 * Counts occupied CSS grid track units for the visible collection grid.
 * Content cards only contribute when shown, and only when their position
 * maps to a currently loaded product index (same as CollectionGrid.view).
 *
 * Uses total track occupancy (not a dense-pack layout simulation). For the
 * product + full-row / single-track content-card cases we ship, `tracks %
 * columns !== 0` still means the painted grid has an empty cell.
 */
export function getCollectionGridOccupiedTracks({
  contentCardPositions,
  contentCardTrackSpan,
  productCount,
  showContentCards,
}: {
  contentCardPositions: ReadonlyArray<string>;
  contentCardTrackSpan: 1 | 2;
  productCount: number;
  showContentCards: boolean;
}): number {
  if (productCount <= 0) {
    return 0;
  }

  let tracks = productCount;
  if (!showContentCards) {
    return tracks;
  }

  const placedPositions = new Set<number>();
  for (const position of contentCardPositions) {
    const index = Number.parseInt(position, 10);
    if (
      Number.isFinite(index) &&
      index >= 0 &&
      index < productCount &&
      !placedPositions.has(index)
    ) {
      placedPositions.add(index);
      tracks += contentCardTrackSpan;
    }
  }

  return tracks;
}

/**
 * 1-indexed visual position of each product in render order.
 * A content card is placed immediately after the product whose index matches
 * its position. Dense packing leaves a hole when the next item does not fit
 * on the current row and lets a later 1-track product fill it.
 */
export function getCollectionGridProductPositions({
  columns,
  contentCardPositions,
  contentCardTrackSpan,
  dense,
  productCount,
  showContentCards,
}: {
  columns: number;
  contentCardPositions: ReadonlyArray<string>;
  contentCardTrackSpan: 1 | 2;
  dense: boolean;
  productCount: number;
  showContentCards: boolean;
}): string[] {
  if (productCount <= 0) {
    return [];
  }

  const cardIndexes = new Set<number>();
  if (showContentCards) {
    for (const position of contentCardPositions) {
      const index = Number.parseInt(position, 10);
      if (
        Number.isFinite(index) &&
        index >= 0 &&
        index < productCount &&
        !cardIndexes.has(index)
      ) {
        cardIndexes.add(index);
      }
    }
  }

  const occupied: boolean[] = [];
  let cursor = 0;

  const fitsAt = (start: number, span: number): boolean => {
    const column = start % columns;
    if (column + span > columns) {
      return false;
    }

    for (let offset = 0; offset < span; offset += 1) {
      if (occupied[start + offset]) {
        return false;
      }
    }

    return true;
  };

  const place = (span: 1 | 2): number => {
    let start = dense ? 0 : cursor;
    while (!fitsAt(start, span)) {
      const column = start % columns;
      if (!dense && column + span > columns) {
        start += columns - column;
      } else {
        start += 1;
      }
    }

    for (let offset = 0; offset < span; offset += 1) {
      occupied[start + offset] = true;
    }

    if (!dense) {
      cursor = start + span;
    }

    return start;
  };

  const positions: string[] = [];
  for (let productIndex = 0; productIndex < productCount; productIndex += 1) {
    positions.push(String(place(1) + 1));
    if (cardIndexes.has(productIndex)) {
      place(contentCardTrackSpan);
    }
  }

  return positions;
}

type CollectionGridImpressionPrevious = {
  count: number;
  requestId: string;
};

/**
 * Whether a settled grid should emit an impression, and the product index
 * where that batch starts. A pending top-up or a repeated request emits
 * nothing. The first request starts at 0, including a zero-result grid and
 * an append. A replaced list starts at 0. A later page starts at previous.count.
 */
export function shouldHoldGridReplacement({
  impressedRequestId,
  listReplaced,
  pendingTopUp,
  settledRequestId,
}: {
  impressedRequestId: string | undefined;
  listReplaced: boolean;
  pendingTopUp: boolean;
  settledRequestId: string;
}): boolean {
  return (
    pendingTopUp && listReplaced && impressedRequestId !== settledRequestId
  );
}

export function nextCollectionGridImpression({
  pendingTopUp,
  previous,
  replaced,
  settledRequestId,
}: {
  pendingTopUp: boolean;
  previous: CollectionGridImpressionPrevious | null;
  replaced: boolean;
  settledRequestId: string;
}): { start: number } | null {
  if (pendingTopUp) {
    return null;
  }

  if (previous !== null && previous.requestId === settledRequestId) {
    return null;
  }

  if (previous === null || replaced) {
    return { start: 0 };
  }

  return { start: previous.count };
}

type CollectionGridDeficitParams = {
  contentCardPositions: ReadonlyArray<string>;
  isBelowMd: boolean;
  isBelowMg: boolean;
  productCount: number;
  showContentCards: boolean;
};

/**
 * How many products to fetch to complete the last CSS row when content cards
 * add track units. Prefer this over another full `pageSize` page — full pages
 * divisible by column count never clear a content-card remainder (e.g. 12+1 → 24+1).
 *
 * Returns 0 when content cards are off or none are in the loaded range — those
 * cases keep legacy full-page Load More instead of silent deficit top-up.
 */
export function getCollectionGridProductDeficit({
  contentCardPositions,
  isBelowMd,
  isBelowMg,
  productCount,
  showContentCards,
}: CollectionGridDeficitParams): number {
  // Matches CollectionGrid / CollectionContentCard CSS breakpoints.
  const columns = isBelowMg ? 2 : 3;
  const contentCardTrackSpan = isBelowMd ? 2 : 1;
  const occupiedTracks = getCollectionGridOccupiedTracks({
    productCount,
    contentCardPositions,
    showContentCards,
    contentCardTrackSpan,
  });
  const isLastRowComplete =
    occupiedTracks === 0 || occupiedTracks % columns === 0;

  if (
    !showContentCards ||
    isLastRowComplete ||
    occupiedTracks === 0 ||
    // No in-range card contribution — not a content-card remainder.
    occupiedTracks <= productCount
  ) {
    return 0;
  }

  return columns - (occupiedTracks % columns);
}

/**
 * Extracts a collection handle from a pathname, including locale-prefixed paths
 * such as `/en/collections/cookware` or `/en-fr/collections/cookware`.
 */
export const getCollectionHandleFromPathname = (
  pathname: string
): string | null => {
  const match = pathname.match(/\/collections\/([^/]+)/);
  return match?.[1] ?? null;
};

/**
 * Builds a collection page path that keeps the current market or language
 * subfolder (e.g. `/en-fr/collections/lavender`).
 */
export const getCollectionPath = (handle: string): string =>
  `${
    typeof window !== "undefined" ? window.Shopify?.routes?.root ?? "/" : "/"
  }collections/${handle}`;

/**
 * Soft-loads to a new collection by updating the URL and history state
 * @param collectionUrl - The path or absolute URL of the collection to navigate to
 * @param skipPushState - Whether to skip pushState (for back navigation)
 */
export const softLoadToCollection = async (
  collectionUrl: string,
  skipPushState = false
): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const newUrl = new URL(collectionUrl, window.location.origin).pathname;

    if (!skipPushState) {
      // Use history.pushState for proper back button functionality
      window.history.pushState({}, "", newUrl);
    } else {
      // Use replaceState for back navigation (popstate events)
      window.history.replaceState(null, "", newUrl);
    }
  } catch (error) {
    console.error("Collection navigation error:", error);
  }
};
